import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Building2, Plus, Loader2, ArrowUpRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { OrgFormFields, emptyOrg, formToOrgPayload } from "@/components/OrgFormFields";

export default function Organizations() {
  const meta = useMeta();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyOrg);

  const load = () => api.get("/organizations").then(({ data }) => setOrgs(data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/organizations", formToOrgPayload(form));
      toast.success("Organization created");
      setOpen(false);
      setForm(emptyOrg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (e, o) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${o.name}"? This permanently removes the organization and all its searches, job postings, invoices and contracts. Linked user accounts are kept but detached. This cannot be undone.`)) return;
    try {
      await api.delete(`/organizations/${o.id}`);
      toast.success("Organization deleted");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader overline="Client accounts" title="Organizations">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-org-btn" className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white transition-colors">
              <Plus className="h-4 w-4 mr-1" /> New
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F4F4F5] border-black/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">New Organization</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <OrgFormFields form={form} setForm={setForm} meta={meta} />
              <DialogFooter>
                <Button type="submit" data-testid="org-save-btn" disabled={saving} className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-8">
        {!orgs && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {orgs && orgs.length === 0 && <Empty icon={Building2} title="No organizations yet" hint="Create your first client organization to start running searches." />}
        {orgs && orgs.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {orgs.map((o) => (
              <div
                key={o.id}
                data-testid={`org-card-${o.id}`}
                onClick={() => navigate(`/app/organizations/${o.id}`)}
                className="group bg-white p-6 text-left hover:bg-[#F4F4F5] transition-colors duration-150 cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 bg-black/10 flex items-center justify-center font-heading font-black text-lg">
                    {o.name.charAt(0)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={(e) => remove(e, o)}
                      data-testid={`org-delete-${o.id}`}
                      title="Delete organization"
                      className="p-1.5 text-muted-foreground/40 hover:text-[#EF4444] transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground/0 group-hover:text-primary transition-colors" />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg mt-4">{o.name}</h3>
                <p className="overline text-[0.55rem] mt-1">{(o.type || "").replace(/_/g, " ")}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {(o.industry_tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-[0.65rem] font-mono px-2 py-0.5 border border-black/10 text-muted-foreground">{t}</span>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-4">{o.search_count} active search{o.search_count === 1 ? "" : "es"}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
